import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    Shield,
    Save,
    Check,
    LayoutDashboard,
    Home,
    Building2,
    Users,
    Settings,
    FileText,
    Briefcase,
    Globe,
    ImageIcon,
    Gavel,
    Award,
    Heart,
    Info,
    MapPin,
    HelpCircle,
    MessageSquare
} from 'lucide-react';

const RolePermissionsPage = () => {
    const [roles, setRoles] = useState(['admin', 'staff', 'editor']);
    const [selectedRole, setSelectedRole] = useState('editor');
    const [permissionsData, setPermissionsData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Permission Groups Configuration
    const permissionGroups = [
        {
            title: 'General',
            icon: <LayoutDashboard className="w-5 h-5" />,
            permissions: [
                { key: 'dashboard', label: 'View Dashboard' }
            ]
        },
        {
            title: 'Home Page',
            icon: <Home className="w-5 h-5" />,
            permissions: [
                { key: 'home_banners', label: 'Manage Banners' },
                { key: 'home_why_choose', label: 'Manage Why Choose Us' },
                { key: 'home_products', label: 'Manage Products' },
                { key: 'home_emi', label: 'Manage EMI Calculator' },
                { key: 'home_scorecard', label: 'Manage Scorecard' },
                { key: 'home_testimonials', label: 'Manage Testimonials' },
                { key: 'home_news', label: 'Manage Important News' }
            ]
        },
        {
            title: 'Loans',
            icon: <Building2 className="w-5 h-5" />,
            permissions: [
                { key: 'loan_construction', label: 'Construction Loan' },
                { key: 'loan_purchase', label: 'Home Purchase Loan' },
                { key: 'loan_composite', label: 'Composite Loan' },
                { key: 'loan_renovation', label: 'Renovation Loan' },
                { key: 'loan_extension', label: 'Extension Loan' },
                { key: 'loan_balance_transfer', label: 'Balance Transfer Loan' },
                { key: 'loan_topup', label: 'Top-up Loan' },
                { key: 'loan_mortgage', label: 'Mortgage Loan' }
            ]
        },
        {
            title: 'Partner Us',
            icon: <Users className="w-5 h-5" />,
            permissions: [
                { key: 'partner_us', label: 'Manage Partner Us Section' }
            ]
        },
        {
            title: 'Investor Info',
            icon: <Globe className="w-5 h-5" />,
            permissions: [
                { key: 'investor_info', label: 'Manage Investor Info' }
            ]
        },
        {
            title: 'Risk Classification',
            icon: <Shield className="w-5 h-5" />,
            permissions: [
                { key: 'risk_classification', label: 'Manage Risk Classification' }
            ]
        },
        {
            title: 'Governance',
            icon: <Gavel className="w-5 h-5" />, // Replaced Shield with Gavel to differentiate
            permissions: [
                { key: 'governance', label: 'Manage Governance' }
            ]
        },
        {
            title: 'Secured Assets',
            icon: <Shield className="w-5 h-5" />,
            permissions: [
                { key: 'secured_assets', label: 'Manage Secured Assets' }
            ]
        },
        {
            title: 'DSA Onboarding',
            icon: <Briefcase className="w-5 h-5" />,
            permissions: [
                { key: 'dsa_onboarding', label: 'Manage DSA Onboarding' }
            ]
        },
        {
            title: 'PMAY',
            icon: <Building2 className="w-5 h-5" />,
            permissions: [
                { key: 'pmay', label: 'Manage PMAY' }
            ]
        },
        {
            title: 'Grievances',
            icon: <Shield className="w-5 h-5" />,
            permissions: [
                { key: 'grievances', label: 'Manage Grievances' }
            ]
        },
        {
            title: 'FAQ',
            icon: <HelpCircle className="w-5 h-5" />, // Use HelpCircle which should be imported
            permissions: [
                { key: 'faq', label: 'Manage FAQ' }
            ]
        },
        {
            title: 'E-Auction',
            icon: <Gavel className="w-5 h-5" />,
            permissions: [
                { key: 'e_auction', label: 'Manage E-Auction' }
            ]
        },
        {
            title: 'Rating Certificate',
            icon: <Award className="w-5 h-5" />,
            permissions: [
                { key: 'rating_certificate', label: 'Manage Rating Certificate' }
            ]
        },
        {
            title: 'Life at Aham',
            icon: <Heart className="w-5 h-5" />,
            permissions: [
                { key: 'life_at_aham', label: 'Manage Life at Aham' }
            ]
        },
        {
            title: 'Gallery',
            icon: <ImageIcon className="w-5 h-5" />,
            permissions: [
                { key: 'gallery', label: 'Manage Gallery' }
            ]
        },
        {
            title: 'About Aham',
            icon: <Info className="w-5 h-5" />,
            permissions: [
                { key: 'about_aham', label: 'Manage About Aham' }
            ]
        },
        {
            title: 'Other Modules',
            icon: <Briefcase className="w-5 h-5" />,
            permissions: [
                { key: 'partnership', label: 'Partnership' },
                { key: 'career', label: 'Career' },
                { key: 'branch_locator', label: 'Branch Locator' },
                 { key: 'blog', label: 'Blog' },
                { key: 'resources', label: 'Resources' }
            ]
        },
        {
            title: 'Enquiries',
            icon: <MessageSquare className="w-5 h-5" />,
            permissions: [
                { key: 'enquiries_contact', label: 'Contact Us Enquiries' },
                { key: 'enquiries_credit_score', label: 'Credit Score Enquiries' },
                { key: 'enquiries_eligibility', label: 'Eligibility Enquiries' },
                { key: 'enquiries_dsa', label: 'DSA Partner Enquiries' },
                { key: 'enquiries_auction', label: 'Auction Enquiries' },
                { key: 'enquiries_dsa_partnership', label: 'DSA Partnership Enquiries' },

                 { key: 'enquiries_grievance', label: 'Grievance Enquiries' },
                { key: 'enquiries_life_at_aham', label: 'Life at Aham Enquiries' }
            ]
        },
        {
            title: 'Settings',
            icon: <Settings className="w-5 h-5" />,
            permissions: [
                { key: 'settings_report', label: 'System Report' },
                { key: 'settings_users', label: 'User Management' },
                { key: 'settings_permissions', label: 'Role Permissions' }
            ]
        }
    ];

    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/role-permissions');
            if (response.data.success) {
                // Transform array to object { role: permissionsObj }
                const dataMap = {};
                response.data.data.forEach(item => {
                    dataMap[item.role] = item.permissions;
                });
                setPermissionsData(dataMap);
            }
        } catch (error) {
            console.error('Failed to fetch permissions:', error);
            toast.error('Failed to load permissions');
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionChange = (role, key) => {
        setPermissionsData(prev => {
            const rolePermissions = prev[role] || {};
            return {
                ...prev,
                [role]: {
                    ...rolePermissions,
                    [key]: !rolePermissions[key]
                }
            };
        });
    };

    const handleSelectAll = (role, groupPermissions, isSelect) => {
        setPermissionsData(prev => {
            const rolePermissions = { ...(prev[role] || {}) };
            groupPermissions.forEach(p => {
                rolePermissions[p.key] = isSelect;
            });
            return {
                ...prev,
                [role]: rolePermissions
            };
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const permissionsToSave = permissionsData[selectedRole];
            await axios.put(`/api/admin/role-permissions/${selectedRole}`, {
                permissions: permissionsToSave
            });
            toast.success('Permissions updated successfully');
        } catch (error) {
            console.error('Failed to save permissions:', error);
            toast.error('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#283079]"></div>
            </div>
        );
    }

    // Role Display Name Mapping
    const roleNames = {
        'admin': 'Admin',
        'staff': 'Staff',
        'editor': 'Editor'
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">User Role Permissions</h1>
                    <p className="text-slate-500 mt-1">Manage system access levels and user capabilities</p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-[#283079] text-white rounded-xl hover:bg-[#202660] transition-colors disabled:opacity-50"
                >
                    {saving ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <Save size={20} />
                    )}
                    <span className="font-medium">Save Changes</span>
                </button>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Role Selector Sidebar */}
                <div className="lg:w-64 flex-shrink-0">
                    <div className="bg-white rounded-2xl shadow-sm p-4 sticky top-6">
                        <div className="flex items-center gap-2 px-4 py-2 mb-2 text-sm font-semibold text-slate-500 uppercase tracking-wider">
                            <Users size={16} />
                            Select Role
                        </div>
                        <div className="space-y-1">
                            {roles.map(role => (
                                <button
                                    key={role}
                                    onClick={() => setSelectedRole(role)}
                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 ${selectedRole === role
                                        ? 'bg-[#283079] text-white shadow-lg shadow-[#283079]/20 font-bold'
                                        : 'text-slate-600 hover:bg-slate-50 font-medium'
                                        }`}
                                >
                                    <span>{roleNames[role] || role}</span>
                                    {selectedRole === role && <Check size={16} />}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Permissions Grid */}
                <div className="flex-1 space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                Permissions for <span className="text-[#283079]">{roleNames[selectedRole]}</span>
                            </h2>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        const allKeys = permissionGroups.flatMap(g => g.permissions);
                                        handleSelectAll(selectedRole, allKeys, true);
                                    }}
                                    className="text-sm font-semibold text-[#283079] hover:bg-slate-50 px-3 py-1.5 rounded-lg transition-colors border border-transparent hover:border-slate-200"
                                >
                                    Select All
                                </button>
                                <button
                                    onClick={() => {
                                        const allKeys = permissionGroups.flatMap(g => g.permissions);
                                        handleSelectAll(selectedRole, allKeys, false);
                                    }}
                                    className="text-sm font-semibold text-slate-500 hover:bg-slate-50 px-3 py-1.5 rounded-lg transition-colors border border-transparent hover:border-slate-200"
                                >
                                    Deselect All
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {permissionGroups.map((group, idx) => (
                                <div key={idx} className="border border-slate-100 rounded-xl p-4 hover:border-slate-200 transition-colors bg-slate-50/50">
                                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100">
                                        <div className="p-2 bg-white rounded-lg shadow-sm text-[#283079]">
                                            {group.icon}
                                        </div>
                                        <h3 className="font-bold text-slate-700">{group.title}</h3>
                                    </div>
                                    <div className="space-y-3">
                                        {group.permissions.map(perm => (
                                            <label key={perm.key} className="flex items-start gap-3 cursor-pointer group/item">
                                                <div className="relative flex items-center mt-0.5">
                                                    <input
                                                        type="checkbox"
                                                        className="peer appearance-none w-5 h-5 border-2 border-slate-300 rounded bg-white checked:bg-[#283079] checked:border-[#283079] transition-all cursor-pointer"
                                                        checked={permissionsData[selectedRole]?.[perm.key] === true}
                                                        onChange={() => handlePermissionChange(selectedRole, perm.key)}
                                                    />
                                                    <Check size={12} className="absolute inset-0 m-auto text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3} />
                                                </div>
                                                <span className="text-sm font-medium text-slate-600 group-hover/item:text-slate-900 transition-colors select-none">
                                                    {perm.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RolePermissionsPage;
